<template>
  <AppLayout>
    <Head :title="board.name" />

    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
	  <projectMenu :project="project"></projectMenu>
      <div class="widget-content">
		  <div class="readonly-notice">
            <i class="bi bi-eye me-2"></i>
            <strong>Tryb tylko do odczytu.</strong>
            Możesz przeglądać karty i ich szczegóły. Edycja oraz przenoszenie zadań są wyłączone.
          </div>


        <div class="kanban-filter-toolbar">
          <button
            type="button"
            class="kanban-filter-toggle"
            :aria-expanded="filtersOpen"
            @click="filtersOpen = !filtersOpen"
          >
            <i class="bi bi-sliders me-2"></i>
            {{ filtersOpen ? 'Ukryj filtry' : 'Filtry' }}
          </button>

          <button
            v-if="filtersOpen"
            type="button"
            class="kanban-filter-clear"
            @click="clearFilters"
          >
            <i class="bi bi-x-circle me-2"></i>
            Wyczyść filtry
          </button>
        </div>
</div>
        <div class="kanban-filter-layout" :class="{ 'filters-open': filtersOpen }">
          <div class="kanban-filter-board">
            <div class="widget-content">
              <div class="kanban-board">
                <div class="kanban-columns">
                  <article
                    v-for="column in filteredColumns"
                    :key="column.id"
                    class="kanban-column"
                  >
                    <header class="kanban-column-header">
                      <div class="d-flex align-items-center gap-2">
                        <span
                          class="kanban-column-color"
                          :style="{ backgroundColor: column.color || '#64748b' }"
                        />
                        <h5 class="mb-0">{{ column.name }}</h5>
                      </div>

                      <span class="badge bg-light text-dark">
                        {{ column.cards.length }}
                      </span>
                    </header>

                    <div class="kanban-cards">
                      <article
                        v-for="card in column.cards"
                        :key="card.id"
                        class="kanban-card kanban-card-readonly"
                        role="button"
                        tabindex="0"
                        @click="openCardDetails(card)"
                        @keydown.enter.prevent="openCardDetails(card)"
                        @keydown.space.prevent="openCardDetails(card)"
                      >
                        <div class="d-flex align-items-start justify-content-between gap-2">
                          <div class="d-flex align-items-center gap-2 min-w-0">
                            <span
                              v-if="card.project_id"
                              class="kanban-project-number"
                              :title="`Projekt ${projectNumber(card.project_id)}`"
                            >
                              {{ projectNumber(card.project_id) }}
                            </span>

                            <h6 class="mb-0 fw-semibold">{{ card.title }}</h6>
                          </div>
                        </div>

                        <p
                          v-if="card.description"
                          class="mt-2 mb-0 text-muted small kanban-card-description"
                        >
                          {{ card.description }}
                        </p>

                        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                          <span
                            v-if="Number(card.comments_count ?? card.comments?.length ?? 0) > 0"
                            class="kanban-card-info text-primary"
                            title="Karta ma komentarze"
                          >
                            <i class="bi bi-chat-dots-fill"></i>
                            {{ card.comments_count ?? card.comments.length }}
                          </span>

                          <span v-if="isOverdue(card, column)" class="kanban-card-info kanban-overdue">
                            <i class="bi bi-exclamation-circle-fill"></i>
                            Po terminie: {{ overdueDays(card) }}
                            {{ overdueDays(card) === 1 ? 'dzień' : 'dni' }}
                          </span>

                          <span
                            v-if="card.updated_at"
                            class="kanban-card-info kanban-card-edited"
                            :title="`Ostatnia aktualizacja: ${relativeTime(card.updated_at)}`"
                          >
                            <i class="bi bi-clock-history"></i>
                            {{ relativeTime(card.updated_at) }}
                          </span>
                        </div>

                        <div class="d-flex flex-wrap gap-2 mt-3">
                          <span class="badge" :class="priorityClass(card.priority)">
                            {{ priorityLabel(card.priority) }}
                          </span>

                          <span v-if="card.due_at" class="badge bg-light text-dark">
                            <i class="bi bi-calendar-event me-1"></i>
                            {{ formatDate(card.due_at) }}
                          </span>
                        </div>

                        <div v-if="card.labels?.length" class="kanban-card-labels mt-3">
                          <span
                            v-for="label in card.labels"
                            :key="label.id"
                            class="kanban-card-label"
                            :style="{ backgroundColor: label.color || '#64748b' }"
                          >
                            <i v-if="label.icon" :class="label.icon" class="me-1" aria-hidden="true"></i>
                            {{ label.name }}
                          </span>
                        </div>

                        <p v-if="card.assignees?.length" class="mt-3 mb-0 text-muted small">
                          <i class="bi bi-people me-1"></i>
                          {{ card.assignees.map(user => fullName(user)).join(', ') }}
                        </p>

                        <div v-if="Number(card.percent) > 0" class="kanban-progress mt-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="small text-muted">Postęp</span>
                            <span class="small fw-semibold">{{ normalizedPercent(card.percent) }}%</span>
                          </div>

                          <div
                            class="progress"
                            role="progressbar"
                            :aria-valuenow="normalizedPercent(card.percent)"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          >
                            <div
                              class="progress-bar"
                              :style="{ width: `${normalizedPercent(card.percent)}%` }"
                            ></div>
                          </div>
                        </div>

                        <div class="text-muted small mt-3">
                          <i class="bi bi-eye me-1"></i>
                          Kliknij, aby zobaczyć szczegóły
                        </div>
                      </article>

                      <div v-if="column.cards.length === 0" class="kanban-empty">
                        <i class="bi bi-inbox d-block mb-2"></i>
                        Brak kart
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <aside v-if="filtersOpen" class="kanban-filter-sidebar">
            <div class="kanban-filter-panel">
              <div class="kanban-filter-panel-heading">
                <h5 class="mb-0">
                  <i class="bi bi-funnel me-2"></i>
                  Filtry zadań
                </h5>

                <button
                  type="button"
                  class="kanban-filter-close"
                  aria-label="Zamknij filtry"
                  @click="filtersOpen = false"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>

              <div class="kanban-filter-field">
                <label for="filter-project">Projekt</label>
                <select id="filter-project" v-model="filters.project_id" class="form-select">
                  <option value="">Wszystkie projekty</option>
                  <option
                    v-for="project in projects"
                    :key="project.id"
                    :value="String(project.id)"
                  >
                    {{ project.number }}
                  </option>
                </select>
              </div>

              <div class="kanban-filter-field">
                <label>Etykiety</label>
                <div class="kanban-filter-options">
                  <label
                    v-for="label in board.labels || []"
                    :key="label.id"
                    class="kanban-filter-option"
                  >
                    <input
                      v-model="filters.label_ids"
                      type="checkbox"
                      :value="Number(label.id)"
                    />
                    <span>{{ label.name }}</span>
                  </label>

                  <p v-if="!board.labels?.length" class="text-muted small mb-0">
                    Brak etykiet.
                  </p>
                </div>
              </div>

              <div class="kanban-filter-field">
                <label>Przypisana osoba</label>
                <div class="kanban-filter-options">
                  <label
                    v-for="person in availableUsers"
                    :key="person.id"
                    class="kanban-filter-option"
                  >
                    <input
                      v-model="filters.user_ids"
                      type="checkbox"
                      :value="Number(person.id)"
                    />
                    <span>{{ fullName(person) }}</span>
                  </label>

                  <p v-if="!availableUsers.length" class="text-muted small mb-0">
                    Brak pracowników.
                  </p>
                </div>
              </div>

              <div class="kanban-filter-field">
                <label for="filter-due-date">Termin</label>
                <select id="filter-due-date" v-model="filters.due_date" class="form-select">
                  <option value="">Dowolny termin</option>
                  <option value="overdue">Po terminie</option>
                  <option value="24h">Najbliższe 24 godziny</option>
                  <option value="7d">Najbliższe 7 dni</option>
                  <option value="30d">Najbliższe 30 dni</option>
                  <option value="no_due_date">Bez terminu</option>
                </select>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>

    <!-- Modal szczegółów: wyłącznie do odczytu -->
    <div
      v-if="selectedCard"
      class="kanban-modal-backdrop"
      @click.self="closeCardDetails"
      @keydown.esc="closeCardDetails"
    >
      <section
        class="kanban-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-details-title"
        tabindex="-1"
      >
        <header class="kanban-modal-header">
          <div class="min-w-0">
            <h5 id="card-details-title" class="mb-1">Szczegóły zadania</h5>
            <p class="text-muted small mb-0">{{ selectedCard.title }}</p>
          </div>

          <button
            type="button"
            class="btn btn-light btn-sm"
            aria-label="Zamknij"
            @click="closeCardDetails"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <div class="kanban-modal-body">
          <div class="mb-3">
            <span class="badge me-2" :class="priorityClass(selectedCard.priority)">
              {{ priorityLabel(selectedCard.priority) }}
            </span>
            <span v-if="selectedCard.due_at" class="badge bg-light text-dark">
              <i class="bi bi-calendar-event me-1"></i>
              Termin: {{ formatDate(selectedCard.due_at) }}
            </span>
          </div>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-card-text me-2"></i>Opis</h6>
            <p v-if="selectedCard.description" class="details-text">
              {{ selectedCard.description }}
            </p>
            <p v-else class="text-muted small">Brak opisu.</p>
          </section>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-folder2 me-2"></i>Projekt</h6>
            <p v-if="selectedCard.project_id">
              {{ projectNumber(selectedCard.project_id) }}
            </p>
            <p v-else class="text-muted small">Nie przypisano projektu.</p>

            <div v-if="selectedCard.start_at" class="small mb-2">
              <strong>Rozpoczęcie:</strong> {{ formatDate(selectedCard.start_at) }}
            </div>
            <div v-if="selectedCard.due_at" class="small mb-2">
              <strong>Termin:</strong> {{ formatDate(selectedCard.due_at) }}
            </div>

            <div class="small mb-2">
              <strong>Postęp:</strong> {{ normalizedPercent(selectedCard.percent) }}%
            </div>
            <div class="progress">
              <div
                class="progress-bar"
                :style="{ width: `${normalizedPercent(selectedCard.percent)}%` }"
              ></div>
            </div>

            <div v-if="selectedCard.updated_at" class="text-muted small mt-3">
              <i class="bi bi-clock-history me-1"></i>
              Ostatnia aktualizacja: {{ formatDateTime(selectedCard.updated_at) }}
            </div>
          </section>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-people me-2"></i>Przypisane osoby</h6>
            <div v-if="selectedCard.assignees?.length" class="details-list">
              <div v-for="person in selectedCard.assignees" :key="person.id" class="details-list-item">
                <i class="bi bi-person-circle me-2"></i>{{ fullName(person) }}
              </div>
            </div>
            <p v-else class="text-muted small">Nie przypisano osób.</p>
          </section>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-tags me-2"></i>Etykiety</h6>
            <div v-if="selectedCard.labels?.length" class="kanban-card-labels">
              <span
                v-for="label in selectedCard.labels"
                :key="label.id"
                class="kanban-card-label"
                :style="{ backgroundColor: label.color || '#64748b' }"
              >
                <i v-if="label.icon" :class="label.icon" class="me-1" aria-hidden="true"></i>
                {{ label.name }}
              </span>
            </div>
            <p v-else class="text-muted small">Brak etykiet.</p>
          </section>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-chat-left-text me-2"></i>Komentarze</h6>
            <div v-if="selectedCard.comments?.length" class="kanban-comments">
              <article
                v-for="comment in selectedCard.comments"
                :key="comment.id"
                class="kanban-comment"
              >
                <p class="mb-1 fw-semibold small">
                  {{ comment.user?.name || fullName(comment.user) || 'Użytkownik' }}
                  <span v-if="comment.created_at" class="text-muted fw-normal">
                    · {{ formatDateTime(comment.created_at) }}
                  </span>
                </p>
                <p class="mb-0 details-text">{{ comment.body }}</p>
              </article>
            </div>
            <p v-else class="text-muted small">Brak komentarzy.</p>
          </section>

          <section class="kanban-modal-section">
            <h6><i class="bi bi-paperclip me-2"></i>Załączniki</h6>
            <div v-if="selectedCard.attachments?.length" class="kanban-attachments">
              <div
                v-for="attachment in selectedCard.attachments"
                :key="attachment.id"
                class="kanban-attachment"
              >
                <div class="min-w-0">
                  <p class="mb-1 text-break fw-semibold small">
                    <i class="bi bi-file-earmark me-1"></i>
                    {{ attachment.original_name }}
                  </p>
                  <p v-if="attachment.size != null" class="mb-0 text-muted small">
                    {{ (Number(attachment.size) / 1024).toFixed(1) }} KB
                  </p>
                </div>

                <a
                  :href="attachmentDownloadUrl(selectedCard, attachment)"
                  class="btn btn-outline-primary btn-sm flex-shrink-0"
                  @click.stop
                >
                  <i class="bi bi-download me-1"></i>
                  Pobierz
                </a>
              </div>
            </div>
            <p v-else class="text-muted small">Brak załączników.</p>
          </section>

          <div class="d-flex justify-content-end pt-3">
            <button type="button" class="btn btn-secondary" @click="closeCardDetails">
              Zamknij
            </button>
          </div>
        </div>
      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link } from '@inertiajs/vue3'
import projectMenu from "@/Pages/Projects/parts/projectMenu.vue";

export default {
  components: {
    AppLayout,
    Head,
	projectMenu,
    Link,
  },

  props: {
  project: Object,
    projects: {
      type: Array,
      default: () => [],
    },
    board: {
      type: Object,
      required: true,
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedCard: null,
      filtersOpen: false,
      filters: {
        project_id: '',
        label_ids: [],
        user_ids: [],
        due_date: '',
      },
    }
  },

  computed: {
    filteredColumns() {
      const now = new Date()

      return (this.board?.columns || []).map(column => ({
        ...column,
        cards: (column.cards || []).filter(card => {
          if (
            this.filters.project_id !== '' &&
            String(card.project_id ?? '') !== String(this.filters.project_id)
          ) {
            return false
          }

          if (this.filters.label_ids.length) {
            const labelIds = (card.labels || []).map(label => Number(label.id))
            if (!this.filters.label_ids.some(id => labelIds.includes(Number(id)))) {
              return false
            }
          }

          if (this.filters.user_ids.length) {
            const userIds = (card.assignees || []).map(user => Number(user.id))
            if (!this.filters.user_ids.some(id => userIds.includes(Number(id)))) {
              return false
            }
          }

          if (this.filters.due_date === 'no_due_date') {
            if (card.due_at) return false
          } else if (this.filters.due_date) {
            if (!card.due_at) return false

            if (this.filters.due_date === 'overdue') {
              if (!this.isOverdue(card, column)) return false
            } else {
              const due = this.parseDate(card.due_at)
              if (!due) return false

              const hours = (due.getTime() - now.getTime()) / 3600000

              if (this.filters.due_date === '24h' && !(hours >= 0 && hours <= 24)) {
                return false
              }
              if (this.filters.due_date === '7d' && !(hours >= 0 && hours <= 168)) {
                return false
              }
              if (this.filters.due_date === '30d' && !(hours >= 0 && hours <= 720)) {
                return false
              }
            }
          }

          return true
        }),
      }))
    },
  },

  methods: {
    openCardDetails(card) {
      this.selectedCard = card
    },

    closeCardDetails() {
      this.selectedCard = null
    },

    clearFilters() {
      this.filters = {
        project_id: '',
        label_ids: [],
        user_ids: [],
        due_date: '',
      }
    },

    projectNumber(projectId) {
      const project = this.projects.find(
        item => Number(item.id) === Number(projectId)
      )
      return project?.number ?? '?'
    },

    fullName(user) {
      if (!user) return ''

      return user.full_name
        || [user.last_name, user.first_name].filter(Boolean).join(' ')
        || user.name
        || (user.id ? `Użytkownik ${user.id}` : '')
    },

    normalizedPercent(value) {
      return Math.min(100, Math.max(0, Number(value) || 0))
    },

    priorityLabel(priority) {
      return {
        low: 'Niski',
        normal: 'Normalny',
        high: 'Wysoki',
        urgent: 'Pilny',
      }[priority] ?? priority ?? 'Normalny'
    },

    priorityClass(priority) {
      return {
        low: 'bg-secondary',
        normal: 'bg-primary',
        high: 'bg-warning text-dark',
        urgent: 'bg-danger',
      }[priority] ?? 'bg-primary'
    },

    parseDate(value) {
      if (!value) return null

      const text = String(value)
      const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)

      const date = match
        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : new Date(value)

      return Number.isNaN(date.getTime()) ? null : date
    },

    formatDate(value) {
      const date = this.parseDate(value)
      if (!date) return ''

      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date)
    },

    formatDateTime(value) {
      if (!value) return ''

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''

      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },

    getColumnPosition(card, fallbackColumn = null) {
      const column = (this.board?.columns || []).find(item =>
        Number(item.id) === Number(card.board_column_id)
      )

      return column
        ? Number(column.position)
        : fallbackColumn
          ? Number(fallbackColumn.position)
          : null
    },

    isOverdue(card, column = null) {
      if (!card.due_at || this.getColumnPosition(card, column) === 3) {
        return false
      }

      const dueDate = this.parseDate(card.due_at)
      if (!dueDate) return false

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      return dueDate < today
    },

    overdueDays(card) {
      const dueDate = this.parseDate(card.due_at)
      if (!dueDate) return 0

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      return Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / 86400000))
    },

    relativeTime(value) {
      if (!value) return ''

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''

      const days = Math.floor((Date.now() - date.getTime()) / 86400000)

      if (days < 0) return 'przed chwilą'
      if (days === 0) return 'dzisiaj'
      if (days === 1) return '1 dzień temu'
      if (days < 7) return `${days} dni temu`
      if (days < 14) return 'tydzień temu'
      if (days < 30) return `${Math.floor(days / 7)} tyg. temu`
      if (days < 60) return 'miesiąc temu'
      if (days < 365) return `${Math.floor(days / 30)} mies. temu`
      if (days < 730) return 'ponad rok temu'

      return `${Math.floor(days / 365)} lat temu`
    },

attachmentDownloadUrl(card, attachment) {
  const boardId = card.board_id

  if (!boardId || !card.id || !attachment.id) {
    console.error('Brak danych do pobrania załącznika:', {
      board_id: boardId,
      card_id: card.id,
      attachment_id: attachment.id,
    })

    return '#'
  }

  return `/kanban/boards/${boardId}/cards/${card.id}/attachments/${attachment.id}/download`
},
  },
}
</script>

<style scoped>
.readonly-notice {
  padding: 0.85rem 1rem;
  border: 1px solid #f5d98b;
  border-radius: 8px;
  background: #fff8e1;
  color: #765b13;
  font-size: 0.9rem;
}

.kanban-board {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-columns {
  display: flex;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
  min-width: 0;
}

.kanban-column {
  flex: 1 1 0;
  width: auto;
  min-width: 260px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.kanban-column-header h5 {
  font-size: 0.95rem;
  font-weight: 600;
}

.kanban-column-color {
  display: inline-block;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 50%;
}

.kanban-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 60px;
}

.kanban-card {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgb(15 23 42 / 5%);
  transition: box-shadow 0.15s ease;
}

.kanban-card-readonly {
  cursor: pointer;
}

.kanban-card-readonly:hover,
.kanban-card-readonly:focus-visible {
  box-shadow: 0 4px 12px rgb(15 23 42 / 12%);
  outline: 2px solid #93c5fd;
  outline-offset: 1px;
}

.kanban-card-description,
.details-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.kanban-empty {
  padding: 1.5rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #94a3b8;
  text-align: center;
  font-size: 0.85rem;
}

.kanban-empty i {
  font-size: 1.5rem;
}

.kanban-project-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border-radius: 5px;
  background: #ffc107;
  color: #212529;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.kanban-card-info {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  line-height: 1.4;
}

.kanban-overdue {
  color: #dc3545;
  font-weight: 700;
}

.kanban-card-edited {
  color: #64748b;
}

.kanban-card-labels {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.4rem;
}

.kanban-card-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.kanban-progress .progress {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #e9ecef;
}

.kanban-progress .progress-bar {
  border-radius: 999px;
}

.kanban-filter-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin: 1.25rem 0;
}

.kanban-filter-toggle,
.kanban-filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1rem;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 600;
}

.kanban-filter-toggle {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
}

.kanban-filter-clear {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.kanban-filter-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.kanban-filter-layout.filters-open {
  grid-template-columns: minmax(0, 1fr) 290px;
}

.kanban-filter-board,
.kanban-filter-board .widget-content {
  min-width: 0;
}

.kanban-filter-sidebar {
  min-width: 0;
  align-self: stretch;
}

.kanban-filter-panel {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  padding: 1rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
}

.kanban-filter-panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e2e8f0;
}

.kanban-filter-panel-heading h5 {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.kanban-filter-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fff;
  color: #64748b;
}

.kanban-filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
}

.kanban-filter-field > label {
  margin: 0;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 700;
}

.kanban-filter-field .form-select {
  width: 100%;
  min-width: 0;
  border-color: #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
}

.kanban-filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 180px;
  overflow-y: auto;
  padding: 0.1rem 0.15rem;
}

.kanban-filter-option {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.4;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.kanban-filter-option input {
  flex-shrink: 0;
  margin-top: 0.15rem;
  accent-color: #2563eb;
}

.kanban-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  background: rgb(15 23 42 / 55%);
}

.kanban-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 20%);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 20px 60px rgb(15 23 42 / 25%);
}

.kanban-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.kanban-modal-section {
  padding: 1.25rem 0;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-section:last-of-type {
  border-bottom: 0;
}

.kanban-modal-section h6 {
  margin-bottom: 0.85rem;
  font-weight: 600;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.details-list-item {
  padding: 0.65rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.kanban-attachments,
.kanban-comments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kanban-attachment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.kanban-comment {
  padding: 0.875rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 991.98px) {
  .kanban-filter-layout.filters-open {
    grid-template-columns: minmax(0, 1fr) 260px;
    gap: 0.75rem;
  }

  .kanban-filter-panel {
    padding: 0.85rem;
  }
}

@media (max-width: 767.98px) {
  .kanban-columns {
    width: max-content;
    min-width: 100%;
  }

  .kanban-column {
    flex: 0 0 280px;
    width: 280px;
    min-width: 280px;
  }

  .kanban-filter-layout.filters-open {
    grid-template-columns: minmax(0, 1fr);
  }

  .kanban-filter-sidebar {
    grid-row: 1;
  }

  .kanban-filter-panel {
    position: static;
    max-height: none;
  }

  .kanban-filter-board {
    grid-row: 2;
  }

  .kanban-modal-backdrop {
    align-items: flex-start;
    padding: 0.5rem;
  }

  .kanban-modal {
    max-height: 96vh;
  }

  .kanban-modal-header,
  .kanban-modal-body {
    padding: 1rem;
  }

  .kanban-attachment {
    align-items: flex-start;
  }
}
</style>